// Single source of truth for device-platform detection across the web-play app.
// Before this util, Play.jsx, Profile.jsx and AppShell.jsx each rolled their own
// iOS/Android/desktop checks — subtly divergent, which let an iPhone user catch
// an Android (Add-to-Home-Screen) prompt or vice versa. Consolidating here keeps
// the install-funnel device-gating consistent: iPhone users only ever see
// iOS-app prompts, Android users only see web-app (PWA / Home-Screen) prompts.
//
// All checks guard `typeof navigator !== 'undefined'` so they're SSR/import-safe.

/**
 * True for the iOS family (real native app available). iPadOS 13+ reports a Mac
 * UA, so disambiguate via touch points — that heuristic is preserved exactly
 * from the original Play.jsx / Profile.jsx implementations.
 */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    // iPadOS 13+ reports a Mac UA; disambiguate via touch points.
    (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1)
  );
}

/** True for Android (no native app — gets the PWA / Add-to-Home-Screen path). */
export function isAndroid() {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent || '');
}

/** Desktop = neither iOS nor Android (gets the in-browser PWA install path). */
export function isDesktop() {
  if (typeof navigator === 'undefined') return false;
  return !isIOS() && !isAndroid();
}

/** True when the app is running as an installed PWA (home screen / dock). */
export function isStandalone() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  return (
    window.navigator.standalone === true ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  );
}

/**
 * True inside an in-app webview (Messages-adjacent apps: Facebook, Instagram,
 * Messenger, TikTok, Snapchat, LinkedIn, Twitter/X, Line, WeChat, Gmail's GSA).
 * These webviews block or break OAuth popups (`linkWithPopup` opens nothing or
 * an orphaned window), so the save sheet uses this to show an "open in your
 * browser" escape hatch instead of letting Apple/Google taps silently fail.
 * UA sniffing is inherently best-effort — false negatives just mean the user
 * sees the normal sheet, so err on the specific side.
 */
export function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|FB_IAB|Instagram|Messenger|Snapchat|TikTok|musical_ly|LinkedInApp|Twitter|Line\/|MicroMessenger|GSA\//i.test(
    ua
  );
}
